import { fetchQuery } from "react-relay";
import {
    Environment,
    Store,
    RecordSource,
    Network,
    FetchFunction
} from "relay-runtime";
import { AnyVariables } from "urql";

import type { pricingPageRelayQuery$data } from "@/__generated_contentful__/pricingPageRelayQuery.graphql";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { SupportedLocales } from "..";
import { AGGClientV2 } from "../../clients/agg-client/agg-client-v2";
import {
    PricingPageRelayQuery,
    getPricingPageRelayQueryVars,
    shapePricingContentfulResponse
} from "../../contentful/graphql/pricing-page";
import { WAC_TO_CPAPI_LOCALE_MAP } from "../content-queries/constants";

import {
    GET_PRICING_CONTENT,
    PricingContentResponse,
    CallToAction,
    PricingPlanComparison,
    PricingPlanComparisonCta,
    ContentPlatformHighlightedFeature,
    Features,
    PlanAdditionalDetails,
    PricingContent
} from "./get-pricing-content-gql.ts";

export type {
    PricingPlanComparison,
    CallToAction,
    PricingPlanComparisonCta,
    ContentPlatformHighlightedFeature,
    PlanAdditionalDetails,
    Features,
    PricingContent
};

export interface GetPricingContentOptions {
    locale?: SupportedLocales;
    pricingTitleInternal?: string;
}

const getPricingContentDeprecated = async (
    productId: string,
    opts: GetPricingContentOptions = {}
): Promise<PricingContent | undefined> => {
    const { locale = "en", pricingTitleInternal } = opts;

    const aggClient = new AGGClientV2({
        gatewayUrl:
            process.env["NODE_ENV"] === "production"
                ? "https://www.atlassian.com/gateway/api/graphql"
                : "https://wac.stg.internal.atlassian.com/gateway/api/graphql"
    });

    const cpapiLocale = WAC_TO_CPAPI_LOCALE_MAP[locale] || "en-US";

    const { data, error } = await aggClient.query(GET_PRICING_CONTENT, {
        search: {
            queries: [
                {
                    withLocales: [cpapiLocale],
                    where: {
                        fieldNamed: "relatedProduct.productNameId",
                        havingValues: [productId]
                    }
                }
            ]
        }
    } as AnyVariables);
    if (error) {
        const errors = error.message.split("\n");
        errors.forEach((errorMessage) => {
            if (errorMessage !== "[GraphQL] String cannot represent value: {}")
                throw new Error(error.message);
        });
    }

    const edges = (data as PricingContentResponse)?.pricings?.edges;

    if (pricingTitleInternal) {
        const result = edges?.find(
            (edge) => edge.node.pricingTitleInternal === pricingTitleInternal
        );
        if (result?.node) {
            return result.node;
        }
    }

    return edges?.[0]?.node;
};

export const getPricingContent = async (
    productId: string,
    opts: GetPricingContentOptions = {}
): Promise<PricingContent | undefined> => {
    if (!checkFeatureFlagGate(featureFlags.wacCpapiDeprecation)) {
        return getPricingContentDeprecated(productId, opts);
    } else {
        const fetchGraphQL: FetchFunction = async (request, variables) => {
            const resp = await fetch(
                "https://graphql.contentful.com/content/v1/spaces/xjcz23wx147q/environments/master",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization":
                            "Bearer fTu2YoVb3vqTOyvfOZveEriN4V4vgNk9aVYcJuLRfc8"
                    },
                    body: JSON.stringify({ query: request.text, variables })
                }
            );
            if (!resp.ok) {
                throw new Error("Response failed.");
            }
            return await resp.json();
        };

        const environment = new Environment({
            store: new Store(new RecordSource({})),
            network: Network.create(fetchGraphQL)
        });

        const pricingDataRaw = await fetchQuery(
            environment,
            PricingPageRelayQuery,
            getPricingPageRelayQueryVars(productId, window.location)
        )
            .toPromise()
            .then((data) => data)
            .catch((error) => {
                console.log(error);
            });

        const pricingDataRelay = shapePricingContentfulResponse(
            pricingDataRaw as pricingPageRelayQuery$data
        );
        return pricingDataRelay?.pricings?.edges?.[0]?.node;
    }
};
